import React from 'react';
import {
  Box,
  Typography,
  Paper,
  FormControlLabel,
  Switch,
  TextField,
  Grid,
  Divider
} from '@mui/material';
import { Horario, HorarioDia, DIAS_SEMANA, HORARIO_DEFAULT } from '../../types/comercio';

interface HorarioEditorProps {
  value: Horario;
  onChange: (horario: Horario) => void;
  disabled?: boolean;
  title?: string;
  subtitle?: string;
}

const HorarioEditor: React.FC<HorarioEditorProps> = ({
  value,
  onChange,
  disabled = false,
  title = "Horario de Atención",
  subtitle
}) => {
  const formatDiaLabel = (dia: string): string => {
    const labels: { [key: string]: string } = {
      lunes: 'Lunes',
      martes: 'Martes',
      miercoles: 'Miércoles',
      jueves: 'Jueves',
      viernes: 'Viernes',
      sabado: 'Sábado',
      domingo: 'Domingo'
    };
    return labels[dia] || dia;
  };

  const handleDiaChange = (dia: string, horarioDia: HorarioDia) => {
    onChange({
      ...value,
      [dia]: horarioDia
    });
  };

  const handleAbiertoChange = (dia: string, abierto: boolean) => {
    const horarioDia = value[dia as keyof Horario];
    handleDiaChange(dia, {
      ...horarioDia,
      abierto,
      hora_apertura: abierto ? (horarioDia.hora_apertura || '08:00') : undefined,
      hora_cierre: abierto ? (horarioDia.hora_cierre || '18:00') : undefined
    });
  };

  const handleHoraChange = (dia: string, campo: 'hora_apertura' | 'hora_cierre', valor: string) => {
    const horarioDia = value[dia as keyof Horario];
    handleDiaChange(dia, {
      ...horarioDia,
      [campo]: valor
    });
  };

  const aplicarHorarioATodos = () => {
    const horarioLunes = value.lunes;
    if (!horarioLunes.abierto) return;

    const nuevoHorario = { ...value };
    DIAS_SEMANA.forEach(dia => {
      if (dia !== 'domingo') { // No aplicar al domingo
        nuevoHorario[dia] = { ...horarioLunes };
      }
    });
    onChange(nuevoHorario);
  };

  const aplicarHorarioLaboral = () => {
    const horarioLaboral = {
      lunes: { abierto: true, hora_apertura: '08:00', hora_cierre: '18:00' },
      martes: { abierto: true, hora_apertura: '08:00', hora_cierre: '18:00' },
      miercoles: { abierto: true, hora_apertura: '08:00', hora_cierre: '18:00' },
      jueves: { abierto: true, hora_apertura: '08:00', hora_cierre: '18:00' },
      viernes: { abierto: true, hora_apertura: '08:00', hora_cierre: '18:00' },
      sabado: { abierto: true, hora_apertura: '08:00', hora_cierre: '14:00' },
      domingo: { abierto: false }
    };
    onChange(horarioLaboral);
  };

  return (
    <Paper elevation={1} sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>
        {title}
      </Typography>
      
      {subtitle && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {subtitle}
        </Typography>
      )}

      {/* Botones de acciones rápidas */}
      <Box sx={{ mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <Typography
          variant="body2"
          component="button"
          onClick={aplicarHorarioLaboral}
          disabled={disabled}
          sx={{
            color: 'primary.main',
            textDecoration: 'underline',
            cursor: 'pointer',
            border: 'none',
            background: 'none',
            padding: 0,
            '&:disabled': { color: 'text.disabled', cursor: 'not-allowed' }
          }}
        >
          Aplicar horario laboral (L-V: 8-18, S: 8-14, D: cerrado)
        </Typography>
        
        <Typography variant="body2" color="text.secondary">•</Typography>
        
        <Typography
          variant="body2"
          component="button"
          onClick={aplicarHorarioATodos}
          disabled={disabled || !value.lunes.abierto}
          sx={{
            color: 'primary.main',
            textDecoration: 'underline',
            cursor: 'pointer',
            border: 'none',
            background: 'none',
            padding: 0,
            '&:disabled': { color: 'text.disabled', cursor: 'not-allowed' }
          }}
        >
          Aplicar horario de lunes a todos los días
        </Typography>
      </Box>

      <Divider sx={{ mb: 3 }} />

      {/* Editor de horarios por día */}
      <Grid sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {DIAS_SEMANA.map((dia) => {
          const horarioDia = value[dia];
          return (
            <Box key={dia}>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 2, 
                p: 2, 
                border: '1px solid',
                borderColor: 'grey.300',
                borderRadius: 1,
                bgcolor: horarioDia.abierto ? 'background.paper' : 'grey.50'
              }}>
                {/* Día y switch */}
                <Box sx={{ minWidth: 120 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={horarioDia.abierto}
                        onChange={(e) => handleAbiertoChange(dia, e.target.checked)}
                        disabled={disabled}
                        color="primary"
                      />
                    }
                    label={formatDiaLabel(dia)}
                    sx={{ margin: 0 }}
                  />
                </Box>

                {/* Horarios de apertura y cierre */}
                {horarioDia.abierto ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
                    <TextField
                      type="time"
                      label="Apertura"
                      value={horarioDia.hora_apertura || ''}
                      onChange={(e) => handleHoraChange(dia, 'hora_apertura', e.target.value)}
                      disabled={disabled}
                      size="small"
                      sx={{ width: 140 }}
                      InputLabelProps={{ shrink: true }}
                    />
                    
                    <Typography variant="body2" color="text.secondary">
                      a
                    </Typography>
                    
                    <TextField
                      type="time"
                      label="Cierre"
                      value={horarioDia.hora_cierre || ''}
                      onChange={(e) => handleHoraChange(dia, 'hora_cierre', e.target.value)}
                      disabled={disabled}
                      size="small"
                      sx={{ width: 140 }}
                      InputLabelProps={{ shrink: true }}
                    />

                    {/* Mostrar duración */}
                    {horarioDia.hora_apertura && horarioDia.hora_cierre && (
                      <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                        ({calcularDuracion(horarioDia.hora_apertura, horarioDia.hora_cierre)} horas)
                      </Typography>
                    )}
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
                    Cerrado
                  </Typography>
                )}
              </Box>
            </Box>
          );
        })}
      </Grid>

      {/* Resumen del horario */}
      <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Resumen de horario:
        </Typography>
        <Typography variant="body2">
          {generarResumenHorario(value)}
        </Typography>
      </Box>
    </Paper>
  );
};

// Función auxiliar para calcular duración
const calcularDuracion = (apertura: string, cierre: string): string => {
  const [horaApertura, minutoApertura] = apertura.split(':').map(Number);
  const [horaCierre, minutoCierre] = cierre.split(':').map(Number);
  
  const minutosApertura = horaApertura * 60 + minutoApertura;
  let minutosCierre = horaCierre * 60 + minutoCierre;
  
  // Si el cierre es al día siguiente (ej: 02:00)
  if (minutosCierre < minutosApertura) {
    minutosCierre += 24 * 60;
  }
  
  const duracionMinutos = minutosCierre - minutosApertura;
  const horas = Math.floor(duracionMinutos / 60);
  const minutos = duracionMinutos % 60;
  
  return minutos > 0 ? `${horas}.${Math.round(minutos / 60 * 10)}` : `${horas}`;
};

// Función auxiliar para generar resumen
const generarResumenHorario = (horario: Horario): string => {
  const diasAbiertos = DIAS_SEMANA.filter(dia => horario[dia].abierto);
  
  if (diasAbiertos.length === 0) {
    return "Siempre cerrado";
  }
  
  if (diasAbiertos.length === 7) {
    return "Abierto todos los días";
  }
  
  // Agrupar días consecutivos con el mismo horario
  const grupos: string[] = [];
  let grupoActual: string[] = [];
  let horarioActual = '';
  
  DIAS_SEMANA.forEach(dia => {
    const horarioDia = horario[dia];
    if (horarioDia.abierto) {
      const horarioTexto = `${horarioDia.hora_apertura}-${horarioDia.hora_cierre}`;
      
      if (horarioTexto === horarioActual) {
        grupoActual.push(dia);
      } else {
        if (grupoActual.length > 0) {
          grupos.push(formatearGrupo(grupoActual, horarioActual));
        }
        grupoActual = [dia];
        horarioActual = horarioTexto;
      }
    } else {
      if (grupoActual.length > 0) {
        grupos.push(formatearGrupo(grupoActual, horarioActual));
        grupoActual = [];
        horarioActual = '';
      }
    }
  });
  
  if (grupoActual.length > 0) {
    grupos.push(formatearGrupo(grupoActual, horarioActual));
  }
  
  return grupos.join(', ');
};

const formatearGrupo = (dias: string[], horario: string): string => {
  const labels: { [key: string]: string } = {
    lunes: 'L', martes: 'M', miercoles: 'Mi', jueves: 'J', 
    viernes: 'V', sabado: 'S', domingo: 'D'
  };
  
  if (dias.length === 1) {
    return `${labels[dias[0]]}: ${horario}`;
  }
  
  return `${labels[dias[0]]}-${labels[dias[dias.length - 1]]}: ${horario}`;
};

export default HorarioEditor;
